import * as React from "react";
import * as XLSX from "xlsx";

interface Ledger {
  ledger: string;
}

interface Expense {
  ledger: Ledger;
  remark?: string;
  amount: number;
  date: string;
  month: string;
  year: string;
  createdAt: string;
  updatedAt: string;
  _id: string;
  __v: number;
}

interface ExportButtonProps {
  data: Expense[];
  selectedPeriod: string;
  className?: string;
  buttonText?: string;
  icon?: React.ReactNode;
}

const useExcelExport = (data: Expense[], selectedPeriod: string) => {
  const filterExpensesByPeriod = (expenses: Expense[]) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    return expenses?.filter((expense: Expense) => {
      const expenseDate = new Date(expense.date);

      switch (selectedPeriod) {
        case "day":
          return expenseDate.toDateString() === currentDate.toDateString();
        case "week":
          const weekAgo = new Date();
          weekAgo.setDate(currentDate.getDate() - 7);
          return expenseDate >= weekAgo && expenseDate <= currentDate;
        case "month":
          return (
            expenseDate.getMonth() === currentMonth &&
            expenseDate.getFullYear() === currentYear
          );
        case "year":
          return expenseDate.getFullYear() === currentYear;
        default:
          return true;
      }
    });
  };

  const calculateTotals = (expenses: Expense[]) => {
    const totals = {
      Personal: 0,
      Salary: 0,
      Office: 0,
      Miscellaneous: 0,
    };

    expenses.forEach((expense) => {
      const category = expense.ledger.ledger.toLowerCase();
      if (category === "personal") totals.Personal += expense.amount;
      else if (category === "salary") totals.Salary += expense.amount;
      else if (category === "office") totals.Office += expense.amount;
      else totals.Miscellaneous += expense.amount;
    });

    const total = Object.values(totals).reduce((acc, curr) => acc + curr, 0);

    return { ...totals, Total: total };
  };

  const exportToExcel = () => {
    if (!data?.length) return;

    const filteredData = filterExpensesByPeriod(data);

    // Transform the data for Excel
    const excelData = filteredData.map((expense: Expense) => ({
      Date: new Date(expense.date).toLocaleDateString(),
      Category: expense.ledger.ledger,
      Description: expense.remark || "",
      Amount: expense.amount,
      Month: expense.month,
      Year: expense.year,
    }));

    // Calculate totals
    const totals = calculateTotals(filteredData);

    // Create a new workbook
    const wb = XLSX.utils.book_new();

    // Create the transactions worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Add some styling to headers
    const headerStyle = {
      font: { bold: true },
      fill: { fgColor: { rgb: "EFEFEF" } },
    };

    // Adjust column widths
    const colWidths = [
      { wch: 15 }, // Date
      { wch: 12 }, // Category
      { wch: 30 }, // Description
      { wch: 12 }, // Amount
      { wch: 10 }, // Month
      { wch: 8 }, // Year
    ];
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Transactions");

    // Create the summary worksheet
    const summaryData = [
      ["Category", "Total Amount"],
      ["Personal", totals.Personal],
      ["Salary", totals.Salary],
      ["Office", totals.Office],
      ["Miscellaneous", totals.Miscellaneous],
      ["Total", totals.Total],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // Generate filename based on selected period
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `expense_report_${selectedPeriod}_${timestamp}.xlsx`;

    // Save the file
    XLSX.writeFile(wb, filename);
  };

  return { exportToExcel };
};

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  selectedPeriod,
  className = "flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors",
  buttonText,
  icon,
}) => {
  const { exportToExcel } = useExcelExport(data, selectedPeriod);

  return (
    <button
      onClick={exportToExcel}
      className={className}
      disabled={!data?.length}
    >
      {icon}
      {buttonText ||
        `Export ${
          selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)
        } Report`}
    </button>
  );
};

export default ExportButton;
